import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Heart } from "lucide-react";
import "../styles/dashboard.css";
import "../styles/VendorOffers.css";
import TopHeader from "../components/TopHeader";
import PremiumModal from "../components/PremiumModal";

const VendorOffers = () => {
    const navigate = useNavigate();
    const { token, status } = useAuth();
    const [offers, setOffers] = useState([]);
    const [fetching, setFetching] = useState(true);
    const [activeTab, setActiveTab] = useState("ALL");
    const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'success', isConfirm: false, onConfirm: null });

    const formatToIST = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            timeZone: 'Asia/Kolkata'
        });
    };

    const filteredOffers = offers.filter(offer => {
        if (activeTab === "ALL") return true;
        return offer.status === activeTab;
    });

    useEffect(() => {
        const fetchOffers = async () => {
            if (!token) return;
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/protected/offers`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setOffers(res.data);
            } catch (err) {
                console.error("Error fetching offers:", err);
            } finally {
                setFetching(false);
            }
        };
        fetchOffers();
    }, [token]);

    const handleDelete = (id) => {
        const offerToDelete = offers.find(o => o.id === id);
        const confirmMsg = offerToDelete?.status === 'PENDING'
            ? "Cancelling this pending offer will credit 1 post back to your account. Proceed?"
            : "Are you sure you want to delete this approved offer? (Note: Posts for approved offers are not credited back)";

        setModal({
            isOpen: true,
            title: "Are you sure?",
            message: confirmMsg,
            type: "warning",
            isConfirm: true,
            onConfirm: () => executeDelete(id)
        });
    };

    const executeDelete = async (id) => {
        try {
            const res = await axios.delete(`${process.env.REACT_APP_API_URL}/protected/offers/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setOffers(offers.map(o => o.id === id ? { ...o, status: 'DELETED' } : o));
            setModal({
                isOpen: true,
                title: "Deleted",
                message: res.data.message,
                type: "success",
                isConfirm: false
            });
        } catch (err) {
            setModal({
                isOpen: true,
                title: "Error",
                message: err.response?.data?.message || "Failed to delete offer",
                type: "error",
                isConfirm: false
            });
        }
    };

    const handleCreateNew = () => {
        if (status === "APPROVED") {
            navigate("/upload");
        } else {
            navigate("/pricing");
        }
    };

    return (
        <div className="dashboard-container">
            <TopHeader />
            <div className="dashboard-content">
                <div className="offers-wrapper">
                    <div className="offers-header">
                        <div>
                            <h1>Manage Offers</h1>
                            <p>Track and manage your product showcases</p>
                        </div>
                        <button className="create-btn" onClick={handleCreateNew}>
                            {status === "APPROVED" ? "+ Create New" : "Choose a Plan"}
                        </button>
                    </div>

                    <div className="offers-tabs">
                        <button
                            className={activeTab === "ALL" ? "active" : ""}
                            onClick={() => setActiveTab("ALL")}
                        >
                            All ({offers.length})
                        </button>
                        <button
                            className={activeTab === "PENDING" ? "active" : ""}
                            onClick={() => setActiveTab("PENDING")}
                        >
                            Submitted ({offers.filter(o => o.status === 'PENDING').length})
                        </button>
                        <button
                            className={activeTab === "APPROVED" ? "active" : ""}
                            onClick={() => setActiveTab("APPROVED")}
                        >
                            Approved ({offers.filter(o => o.status === 'APPROVED').length})
                        </button>
                        <button
                            className={activeTab === "REJECTED" ? "active" : ""}
                            onClick={() => setActiveTab("REJECTED")}
                        >
                            Rejected ({offers.filter(o => o.status === 'REJECTED').length})
                        </button>
                        <button
                            className={activeTab === "DELETED" ? "active" : ""}
                            onClick={() => setActiveTab("DELETED")}
                        >
                            Deleted ({offers.filter(o => o.status === 'DELETED').length})
                        </button>
                    </div>

                    {fetching ? (
                        <div className="loading-state-mini">
                            <div className="loader-dots">
                                <span></span><span></span><span></span>
                            </div>
                            <p>Syncing your offers...</p>
                        </div>
                    ) : offers.length > 0 ? (
                        <div className="offers-grid">
                            {filteredOffers.map((offer) => (
                                <div className="offer-card" key={offer.id}>
                                    <div className="offer-image">
                                        <div className="like-badge">
                                            <Heart size={14} />
                                            <span>{offer.like_count || 0}</span>
                                        </div>
                                        <img src={offer.poster_url || "https://via.placeholder.com/300x200"} alt={offer.title} />
                                        <div className={`status-badge ${offer.status.toLowerCase()}`}>
                                            {offer.status === 'PENDING' ? 'SUBMITTED' : offer.status}
                                        </div>
                                    </div>
                                    <div className="offer-details">
                                        <h3>{offer.title}</h3>
                                        <p className="offer-cat">{offer.category}</p>
                                        <div className="offer-dates">
                                            <span>📅 {formatToIST(offer.start_date)} - {formatToIST(offer.end_date)}</span>
                                        </div>
                                        {offer.status === 'REJECTED' && offer.rejection_reason && (
                                            <div className="rejection-box">
                                                <strong>Reason:</strong> {offer.rejection_reason}
                                            </div>
                                        )}
                                        <div className="offer-actions">
                                            {offer.status !== 'DELETED' ? (
                                                <button className="delete-btn" onClick={() => handleDelete(offer.id)}>Delete</button>
                                            ) : (
                                                <button className="delete-btn disabled" disabled>Deleted</button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="empty-offers-state">
                            <div className="empty-icon">🛍️</div>
                            <h2>No Active Offers</h2>
                            <p>Start showcasing your collections to reach more customers.</p>
                            <div className="empty-actions">
                                <button className="primary-empty-btn" onClick={handleCreateNew}>
                                    {status === "APPROVED" ? "Create First Offer" : "Choose a Plan"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <PremiumModal
                isOpen={modal.isOpen}
                onClose={() => setModal({ ...modal, isOpen: false })}
                title={modal.title}
                message={modal.message}
                type={modal.type}
                isConfirm={modal.isConfirm}
                onConfirm={modal.onConfirm}
            />
        </div>
    );
};

export default VendorOffers;
